import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { useMemo, Suspense, lazy } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { Login } from './pages/Login';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PermissionProtectedRoute } from './components/PermissionProtectedRoute';
import { Permission } from './types';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { OnboardingDialog } from './components/OnboardingDialog';
import { AdminOwnerProtectedRoute } from './components/AdminOwnerProtectedRoute';

// Lazy loading de todas as páginas principais para reduzir bundle inicial
// Usar named exports com wrapper para default export
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const RepositoryView = lazy(() => import('./pages/RepositoryView').then(m => ({ default: m.RepositoryView })));
const Contributors = lazy(() => import('./pages/Contributors').then(m => ({ default: m.Contributors })));
const ContributorDetails = lazy(() => import('./pages/ContributorDetails').then(m => ({ default: m.ContributorDetails })));
const EngineeringOverview = lazy(() => import('./pages/EngineeringOverview').then(m => ({ default: m.EngineeringOverview })));
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const MyContribution = lazy(() => import('./pages/MyContribution').then(m => ({ default: m.MyContribution })));
const Notifications = lazy(() => import('./pages/Notifications').then(m => ({ default: m.Notifications })));
const OrganizationMembers = lazy(() => import('./pages/OrganizationMembers').then(m => ({ default: m.OrganizationMembers })));
const MemberDetails = lazy(() => import('./pages/MemberDetails').then(m => ({ default: m.MemberDetails })));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite').then(m => ({ default: m.AcceptInvite })));
const Incidents = lazy(() => import('./pages/Incidents').then(m => ({ default: m.Incidents })));
const IncidentDetails = lazy(() => import('./pages/IncidentDetails').then(m => ({ default: m.IncidentDetails })));
const EngineeringRepositories = lazy(() => import('./pages/EngineeringRepositories').then(m => ({ default: m.EngineeringRepositories })));
const EngineeringRepositoryDetails = lazy(() => import('./pages/EngineeringRepositoryDetails').then(m => ({ default: m.EngineeringRepositoryDetails })));
const ReliabilityMetrics = lazy(() => import('./pages/ReliabilityMetrics').then(m => ({ default: m.ReliabilityMetrics })));
const SettingsIntegrations = lazy(() => import('./pages/SettingsIntegrations').then(m => ({ default: m.SettingsIntegrations })));
const SettingsRepositories = lazy(() => import('./pages/SettingsRepositories').then(m => ({ default: m.SettingsRepositories })));
const SettingsExtraction = lazy(() => import('./pages/SettingsExtraction').then(m => ({ default: m.SettingsExtraction })));
const SettingsManualSync = lazy(() => import('./pages/SettingsManualSync').then(m => ({ default: m.SettingsManualSync })));
const SettingsPermissions = lazy(() => import('./pages/SettingsPermissions').then(m => ({ default: m.SettingsPermissions })));
const EngineeringActivities = lazy(() => import('./pages/EngineeringActivities').then(m => ({ default: m.EngineeringActivities })));
const PullRequestDetails = lazy(() => import('./pages/PullRequestDetails').then(m => ({ default: m.PullRequestDetails })));
const WorkflowRunDetails = lazy(() => import('./pages/WorkflowRunDetails').then(m => ({ default: m.WorkflowRunDetails })));
const DeploymentDetails = lazy(() => import('./pages/DeploymentDetails').then(m => ({ default: m.DeploymentDetails })));
const MyActivities = lazy(() => import('./pages/MyActivities').then(m => ({ default: m.MyActivities })));
const GoalPlans = lazy(() => import('./pages/GoalPlans').then(m => ({ default: m.GoalPlans })));
const GoalPlanDetail = lazy(() => import('./pages/GoalPlanDetail').then(m => ({ default: m.GoalPlanDetail })));
const GoalPlanAddIndicator = lazy(() => import('./pages/GoalPlanAddIndicator').then(m => ({ default: m.GoalPlanAddIndicator })));
const GoalPlanEditMeta = lazy(() => import('./pages/GoalPlanEditMeta').then(m => ({ default: m.GoalPlanEditMeta })));
const GoalPlanAddView = lazy(() => import('./pages/GoalPlanAddView').then(m => ({ default: m.GoalPlanAddView })));
const Reports = lazy(() => import('./pages/Reports').then(m => ({ default: m.Reports })));
const CreateReport = lazy(() => import('./pages/CreateReport').then(m => ({ default: m.CreateReport })));
const ReportDetail = lazy(() => import('./pages/ReportDetail').then(m => ({ default: m.ReportDetail })));
const BudgetPlatforms = lazy(() => import('./pages/BudgetPlatforms').then(m => ({ default: m.BudgetPlatforms })));
const BudgetPlatformForm = lazy(() => import('./pages/BudgetPlatformForm').then(m => ({ default: m.BudgetPlatformForm })));
const Budget = lazy(() => import('./pages/Budget').then(m => ({ default: m.Budget })));

// Componente de loading para Suspense
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);

function AppContent() {
  const { isDark } = useTheme();

  // MUI Theme sincronizado com o tema do Tabler
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? 'dark' : 'light',
          primary: {
            main: '#7C4DFF', // Brand primary
            dark: '#5930D8',
            light: '#9A7DFF',
          },
          secondary: {
            main: isDark ? '#CBD5E1' : '#475569', // text-secondary
          },
          background: {
            default: isDark ? '#0B1220' : '#FFFFFF',
            paper: isDark ? '#111827' : '#FFFFFF',
          },
          text: {
            primary: isDark ? '#F8FAFC' : '#0B1220',
            secondary: isDark ? '#CBD5E1' : '#475569',
          },
          error: {
            main: '#EF4444',
          },
          warning: {
            main: '#F59E0B',
          },
          success: {
            main: '#22C55E',
          },
          info: {
            main: '#38BDF8', // chart-2
          },
        },
      }),
    [isDark]
  );

  return (
    <MuiThemeProvider theme={muiTheme}>
      <Router>
        <AuthProvider>
          <OrganizationProvider>
            <ProfileProvider>
              <OnboardingDialog />
              <Suspense fallback={<PageLoader />}>
                <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <PermissionProtectedRoute permission={Permission.ViewDashboard}>
                  <Dashboard />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/repository/:id"
              element={
                <ProtectedRoute>
                  <RepositoryView />
                </ProtectedRoute>
              }
            />
            {/* Redirecionamentos das rotas antigas */}
            <Route
              path="/contributors"
              element={<Navigate to="/engineering/contributors" replace />}
            />
            <Route
              path="/repositories"
              element={<Navigate to="/engineering/overview" replace />}
            />
            <Route
              path="/incidents"
              element={<Navigate to="/reliability/incidents" replace />}
            />
            <Route
              path="/organizations/:orgId/members"
              element={<Navigate to="/org/members" replace />}
            />
            <Route
              path="/settings"
              element={<Navigate to="/settings/integrations" replace />}
            />
            {/* Novas rotas - Engenharia */}
            <Route
              path="/engineering/overview"
              element={
                <PermissionProtectedRoute permission={Permission.ViewEngineeringMetrics}>
                  <EngineeringOverview />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/contributors"
              element={
                <PermissionProtectedRoute permission={Permission.ViewContributors}>
                  <Contributors />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/contributors/:contributorName"
              element={
                <PermissionProtectedRoute permission={Permission.ViewContributors}>
                  <ContributorDetails />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/repositories/:id"
              element={
                <PermissionProtectedRoute permission={Permission.ViewRepositories}>
                  <EngineeringRepositoryDetails />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/repositories"
              element={
                <PermissionProtectedRoute permission={Permission.ViewRepositories}>
                  <EngineeringRepositories />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/activities"
              element={
                <AdminOwnerProtectedRoute>
                  <EngineeringActivities />
                </AdminOwnerProtectedRoute>
              }
            />
            <Route
              path="/engineering/activities/pr/:prId"
              element={
                <AdminOwnerProtectedRoute>
                  <PullRequestDetails />
                </AdminOwnerProtectedRoute>
              }
            />
            <Route
              path="/engineering/activities/workflow-run/:runId"
              element={
                <AdminOwnerProtectedRoute>
                  <WorkflowRunDetails />
                </AdminOwnerProtectedRoute>
              }
            />
            <Route
              path="/engineering/activities/deployment/:deploymentId"
              element={
                <AdminOwnerProtectedRoute>
                  <DeploymentDetails />
                </AdminOwnerProtectedRoute>
              }
            />
            <Route
              path="/engineering/goal-plans"
              element={
                <PermissionProtectedRoute permission={Permission.ViewPlanning}>
                  <GoalPlans />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/goal-plans/:id"
              element={
                <PermissionProtectedRoute permission={Permission.ViewPlanning}>
                  <GoalPlanDetail />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/goal-plans/:id/indicators/new"
              element={
                <PermissionProtectedRoute permission={Permission.EditPlanning}>
                  <GoalPlanAddIndicator />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/goal-plans/:id/indicators/:indicatorId/edit-meta"
              element={
                <PermissionProtectedRoute permission={Permission.EditPlanning}>
                  <GoalPlanEditMeta />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/goal-plans/:id/views/new"
              element={
                <PermissionProtectedRoute permission={Permission.EditPlanning}>
                  <GoalPlanAddView />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/reports"
              element={
                <PermissionProtectedRoute permission={Permission.ViewPlanning}>
                  <Reports />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/reports/new"
              element={
                <PermissionProtectedRoute permission={Permission.CreatePlanning}>
                  <CreateReport />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/engineering/reports/:id"
              element={
                <PermissionProtectedRoute permission={Permission.ViewPlanning}>
                  <ReportDetail />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/planning/platforms/new"
              element={
                <PermissionProtectedRoute permission={Permission.ViewPlanning}>
                  <BudgetPlatformForm />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/planning/platforms/:id"
              element={
                <PermissionProtectedRoute permission={Permission.ViewPlanning}>
                  <BudgetPlatformForm />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/planning/platforms"
              element={
                <PermissionProtectedRoute permission={Permission.ViewPlanning}>
                  <BudgetPlatforms />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/planning/budget"
              element={
                <PermissionProtectedRoute permission={Permission.ViewPlanning}>
                  <Budget />
                </PermissionProtectedRoute>
              }
            />
            {/* Novas rotas - Confiabilidade */}
            <Route
              path="/reliability/incidents"
              element={
                <PermissionProtectedRoute permission={Permission.ViewIncidents}>
                  <Incidents />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/reliability/metrics"
              element={
                <PermissionProtectedRoute permission={Permission.ViewReliabilityMetrics}>
                  <ReliabilityMetrics />
                </PermissionProtectedRoute>
              }
            />
            {/* Novas rotas - Organização */}
            <Route
              path="/org/members"
              element={
                <PermissionProtectedRoute permission={Permission.ViewMembers}>
                  <OrganizationMembers />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/org/members/:memberId"
              element={
                <PermissionProtectedRoute permission={Permission.ViewMembers}>
                  <MemberDetails />
                </PermissionProtectedRoute>
              }
            />
            {/* Novas rotas - Configurações */}
            <Route
              path="/settings/integrations"
              element={
                <PermissionProtectedRoute permission={Permission.ManageIntegrations}>
                  <SettingsIntegrations />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/settings/repositories"
              element={
                <PermissionProtectedRoute permission={Permission.ManageRepositories}>
                  <SettingsRepositories />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/settings/extraction"
              element={
                <PermissionProtectedRoute permission={Permission.ManageExtractionConfig}>
                  <SettingsExtraction />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/settings/manual-sync"
              element={
                <PermissionProtectedRoute permission={Permission.ManageManualSync}>
                  <SettingsManualSync />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/settings/permissions"
              element={
                <PermissionProtectedRoute permission={Permission.ManagePermissions}>
                  <SettingsPermissions />
                </PermissionProtectedRoute>
              }
            />
            {/* Rotas existentes mantidas */}
            <Route
              path="/incidents/:id"
              element={<Navigate to="/reliability/incidents/:id" replace />}
            />
            <Route
              path="/reliability/incidents/:id"
              element={
                <PermissionProtectedRoute permission={Permission.ViewIncidents}>
                  <IncidentDetails />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-contribution"
              element={
                <ProtectedRoute>
                  <MyContribution />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-activities"
              element={
                <ProtectedRoute>
                  <MyActivities />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-activities/pr/:prId"
              element={
                <ProtectedRoute>
                  <PullRequestDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accept-invite/:code?"
              element={<AcceptInvite />}
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Suspense>
            </ProfileProvider>
          </OrganizationProvider>
        </AuthProvider>
      </Router>
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
